import type { GameVehicleSummary, WikiVehicle } from "~~/types/gamedata";

export type ShipCompareDirection = "higher" | "lower" | "none";
export type ShipCompareRenderAs = "bar" | "badges" | "text";

export interface ShipCompareBadge {
  label: string;
  value: string;
}

export interface ShipCompareRow {
  key: string;
  label: string;
  unit?: string;
  direction: ShipCompareDirection;
  renderAs: ShipCompareRenderAs;
  /** Roh-/Textwert für die Anzeige, z.B. Hersteller, Produktionsstatus. */
  text?: (summary: GameVehicleSummary, raw: WikiVehicle | null) => string | null;
  /** Numerischer Wert für `renderAs: 'bar'` (steuert Balken + Best/Schlechtest-Markierung). */
  value?: (summary: GameVehicleSummary, raw: WikiVehicle | null) => number | null;
  /** Formatierung des Zahlenwerts, Standard: deutsches Zahlenformat + `unit`. */
  format?: (value: number) => string;
  /** Badges für `renderAs: 'badges'` (z.B. Resistenzen je Schadenstyp). */
  badges?: (summary: GameVehicleSummary, raw: WikiVehicle | null) => ShipCompareBadge[];
}

export interface ShipCompareGroup {
  key: string;
  label: string;
  rows: ShipCompareRow[];
}

function n(value: number, decimals = 0): string {
  return value.toLocaleString("de-DE", { maximumFractionDigits: decimals });
}

/** Klein gehaltene Kopie der Schadenstyp-Labels aus `defense-card/index.vue` — nur für die Resistenz-Badge-Zeilen hier gebraucht. */
const DAMAGE_TYPE_LABELS: Record<string, string> = {
  physical: "Physisch",
  energy: "Energie",
  distortion: "Verzerrung",
  thermal: "Thermisch",
  biochemical: "Biochemisch",
};

function damageLabel(key: string): string {
  return DAMAGE_TYPE_LABELS[key] ?? key.replaceAll("_", " ");
}

function shieldResistanceBadges(raw: WikiVehicle | null): ShipCompareBadge[] {
  return Object.entries(raw?.shield?.resistance ?? {})
    .map(([key, range]) => {
      const percent = range?.maximum ?? range?.minimum;
      if (percent == null) return null;
      return { label: damageLabel(key), value: `${n(percent * 100)}%` };
    })
    .filter((badge): badge is ShipCompareBadge => badge !== null);
}

function armorResistanceBadges(raw: WikiVehicle | null): ShipCompareBadge[] {
  return Object.entries(raw?.armor?.resistance_multiplier ?? {})
    .filter(([key, value]) => !key.endsWith("_change") && value != null)
    .map(([key, value]) => ({ label: damageLabel(key), value: `${n(value!, 2)}×` }));
}

export const SHIP_COMPARE_GROUPS: ShipCompareGroup[] = [
  {
    key: "basis",
    label: "Basis",
    rows: [
      { key: "manufacturer", label: "Hersteller", direction: "none", renderAs: "text", text: (s) => s.manufacturer.name },
      { key: "productionStatus", label: "Produktionsstatus", direction: "none", renderAs: "text", text: (s) => s.productionStatus },
      { key: "role", label: "Fokus", direction: "none", renderAs: "text", text: (s) => s.role },
      { key: "career", label: "Klassifizierung", direction: "none", renderAs: "text", text: (s) => s.career },
      { key: "size", label: "Größe", direction: "none", renderAs: "text", text: (s) => s.sizeLabel },
      { key: "length", label: "Länge", unit: "m", direction: "none", renderAs: "bar", value: (s) => s.dimensions.length },
      { key: "width", label: "Breite", unit: "m", direction: "none", renderAs: "bar", value: (s) => s.dimensions.width },
      { key: "height", label: "Höhe", unit: "m", direction: "none", renderAs: "bar", value: (s) => s.dimensions.height },
      { key: "mass", label: "Masse", unit: "kg", direction: "none", renderAs: "bar", value: (s) => s.mass },
      { key: "cargo", label: "Fracht", unit: "SCU", direction: "higher", renderAs: "bar", value: (s) => s.cargo },
      { key: "msrp", label: "Pledge-Preis", unit: "$", direction: "lower", renderAs: "bar", value: (s) => s.msrp },
    ],
  },
  {
    key: "besatzung",
    label: "Besatzung",
    rows: [
      { key: "crewMin", label: "min. Besatzung", unit: "Person(en)", direction: "none", renderAs: "bar", value: (s) => s.crew.min },
      { key: "crewMax", label: "max. Besatzung", unit: "Person(en)", direction: "higher", renderAs: "bar", value: (s) => s.crew.max },
    ],
  },
  {
    key: "geschwindigkeit",
    label: "Geschwindigkeit",
    rows: [
      { key: "speedScm", label: "SCM", unit: "m/s", direction: "higher", renderAs: "bar", value: (s) => s.speed.scm },
      { key: "speedMax", label: "Max", unit: "m/s", direction: "higher", renderAs: "bar", value: (s) => s.speed.max },
      { key: "pitch", label: "Nicken (Pitch)", unit: "°/s", direction: "higher", renderAs: "bar", value: (_s, r) => r?.agility?.pitch ?? null },
      { key: "yaw", label: "Gieren (Yaw)", unit: "°/s", direction: "higher", renderAs: "bar", value: (_s, r) => r?.agility?.yaw ?? null },
      { key: "roll", label: "Rollen (Roll)", unit: "°/s", direction: "higher", renderAs: "bar", value: (_s, r) => r?.agility?.roll ?? null },
    ],
  },
  {
    key: "kampf",
    label: "Kampf",
    rows: [
      { key: "burstDps", label: "Burst-DPS", unit: "DPS", direction: "higher", renderAs: "bar", value: (_s, r) => r?.weaponry?.pilot_dps ?? null },
      { key: "sustainedDps", label: "Dauer-DPS", unit: "DPS", direction: "higher", renderAs: "bar", value: (_s, r) => r?.weaponry?.pilot_sustained_dps ?? null },
      { key: "alpha", label: "Alpha", unit: "DMG", direction: "higher", renderAs: "bar", value: (_s, r) => r?.weaponry?.pilot_alpha ?? null },
      { key: "missileDamage", label: "Raketenschaden", unit: "DMG", direction: "higher", renderAs: "bar", value: (_s, r) => r?.weaponry?.total_missile_damage ?? r?.weaponry?.missiles?.damage?.total ?? null },
    ],
  },
  {
    key: "verteidigung",
    label: "Verteidigung",
    rows: [
      { key: "shieldHp", label: "Schild-HP", unit: "HP", direction: "higher", renderAs: "bar", value: (_s, r) => r?.shield?.hp ?? null },
      { key: "shieldRegen", label: "Regeneration", unit: "HP/s", direction: "higher", renderAs: "bar", value: (_s, r) => r?.shield?.regeneration ?? null },
      { key: "armorHp", label: "Panzerungs-HP", unit: "HP", direction: "higher", renderAs: "bar", value: (_s, r) => r?.armor?.health ?? null },
      { key: "shieldResistance", label: "Schild-Resistenz", direction: "none", renderAs: "badges", badges: (_s, r) => shieldResistanceBadges(r) },
      { key: "armorResistance", label: "Panzerungs-Resistenz", direction: "none", renderAs: "badges", badges: (_s, r) => armorResistanceBadges(r) },
    ],
  },
];

export function formatShipCompareValue(row: ShipCompareRow, value: number): string {
  const formatted = row.format ? row.format(value) : n(value, 2);
  return row.unit ? `${formatted} ${row.unit}` : formatted;
}
